import { useEffect, useState } from 'react';
import { ApiError, type Api, type TemplateNeedRow } from '../api';
import type { T } from '../i18n';

const DAYS = [7, 30, 90] as const;
const human = (x: string) => (x === '?' ? '—' : x.replaceAll('_', ' '));

/**
 * Platform operators only: what businesses asked the "could not find it?" finder for, across all businesses —
 * labels and counts, never a business or its words. Rows without a ready agent come first: the next templates to write.
 */
export const PlatformNeedsPage = ({ api, t }: { api: Api; t: T }) => {
  const [days, setDays] = useState<(typeof DAYS)[number]>(30);
  const [rows, setRows] = useState<TemplateNeedRow[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    setRows(undefined);
    setError(undefined);
    api.templateNeeds(days).then((r) => setRows(r.rows)).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, days]);
  const max = Math.max(1, ...(rows ?? []).map((r) => r.requests));
  return (
    <section className="stats">
      <header className="page-head">
        <div>
          <h1>{t('platform.needs.title')}</h1>
          <p className="muted">{t('platform.needs.subtitle')}</p>
        </div>
      </header>
      <div className="stats-filters">
        <div className="segmented" role="group" aria-label={t('stats.period')}>
          {DAYS.map((d) => <button key={d} type="button" className={days === d ? 'on' : ''} onClick={() => setDays(d)}>{t('stats.lastDays', { n: d })}</button>)}
        </div>
      </div>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}
      {rows && rows.length === 0 && <div className="empty"><p>{t('platform.needs.empty')}</p></div>}
      {rows && rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead><tr>
                <th>{t('platform.needs.industry')}</th><th>{t('platform.needs.callType')}</th><th>{t('templates.direction')}</th>
                <th>{t('platform.needs.requests')}</th><th className="num">{t('platform.needs.businesses')}</th><th className="num">{t('platform.needs.noMatch')}</th>
                <th className="num">{t('platform.needs.choseTemplate')}</th><th className="num">{t('platform.needs.choseCustom')}</th><th />
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{human(r.industry)}</td>
                    <td>{human(r.callType)}</td>
                    <td>{r.direction === 'inbound' || r.direction === 'outbound' ? t(`direction.${r.direction}`) : '—'}</td>
                    <td><span className="rate-cell"><span className="hbar-track"><span className="hbar-fill" style={{ width: `${(r.requests / max) * 100}%` }} /></span><span className="num">{r.requests}</span></span></td>
                    <td className="num">{r.businesses}</td>
                    <td className="num">{r.noMatch}</td>
                    <td className="num">{r.choseTemplate}</td>
                    <td className="num">{r.choseCustom}</td>
                    <td>{r.noMatch >= 3 && r.businesses >= 2 && <span className="tag own best">{t('platform.needs.write')}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint">{t('platform.needs.hint')}</p>
        </div>
      )}
    </section>
  );
};
